import { useMemo, useState } from 'react';

import {
  Container,
  FAQ,
  HeadCellSortState,
  Link,
  ParsedHtmlContent,
  theme,
  toggleSortStates,
  useMediaMin,
} from '@foradevelopment/fora-ui';
import { PayMonthOverviewViewModel } from '@fora-monorepo/employer-my-pages-common/types';

import { FOKExpirationFeedback, IntroText, ReportProcessingFeedback } from '../../components';
import { useAppSelector } from '../../redux/hooks';
import { PayReportsTable } from '../components/PayReportsTable';
import {
  defaultPayReportsListSortState,
  PayReportsTableSortIdKeys,
  PayReportsTableSortState,
} from '../components/PayReportsTable/PayReportsListSortState';
import { sortPayMonthsListIndices } from '../../common/utils/payReportsSorting';
import { links } from '../../router/links';
import { useTypedTranslation } from '../../i18n/i18n';

export const PayrollStartPage = () => {
  const employerHasEmployments = useAppSelector(state => state.employment.employerHasEmployments);
  const payMonthOverviews = useAppSelector(state => state.payroll.payMonthOverviews);
  const hasPayrollReports = payMonthOverviews.some(payMonthOverView => payMonthOverView.payrollReportHeads.length > 0);

  if (employerHasEmployments || hasPayrollReports) {
    return <StartPageWithEmployments />;
  }
  return <StartPageWithoutEmployments />;
};

const StartPageWithEmployments = () => {
  const isDesktop = useMediaMin(theme.breakpoint.lg);
  const payMonthOverviews: PayMonthOverviewViewModel[] = useAppSelector(state => state.payroll.payMonthOverviews);
  const isAnyReportProcessing = useAppSelector(state => state.payroll.isAnyReportProcessing);
  const faqs = useAppSelector(state => state.content.content.faqListPayrolls);

  const faGroups = useAppSelector(state => state.employer.employerInfo.faGroups);

  const expiringFoks = useMemo(() => faGroups.filter(({ isExpiring }) => isExpiring), [faGroups]);

  const [tableSortState, setTableSortState] = useState<PayReportsTableSortState>({
    ...defaultPayReportsListSortState,
    reportingPeriods: HeadCellSortState.Desc,
  });

  const sortTable = (sortId: PayReportsTableSortIdKeys) =>
    setTableSortState(state => {
      // Reset from initial state to make sure that only one column is sorted at a time
      const newState = { ...defaultPayReportsListSortState };
      newState[sortId] = toggleSortStates(state[sortId]);
      return newState;
    });

  const sortedPayMonthIndices = useMemo(() => {
    return sortPayMonthsListIndices({ payMonthList: payMonthOverviews, sortState: tableSortState });
  }, [payMonthOverviews, tableSortState]);

  return (
    <>
      <IntroTextWithEmployments />
      {isAnyReportProcessing && <ReportProcessingFeedback />}
      {!isAnyReportProcessing && <FOKExpirationFeedback expiringFoks={expiringFoks} />}
      <PayReportsTable
        sortedPayMonthIndices={sortedPayMonthIndices}
        payMonthOverviews={payMonthOverviews}
        sortState={tableSortState}
        sortTable={sortTable}
      />
      {!!faqs.length && <FAQ testId="payrolls-faq" layout={isDesktop ? 'drawer' : 'modal'} questionAnswers={faqs} />}
    </>
  );
};

const IntroTextWithEmployments = () => {
  const ingress = useAppSelector(state => state.content.content.ingressPayrolls);

  const { t } = useTypedTranslation();
  return (
    <IntroText>
      <Container>
        <ParsedHtmlContent htmlContent={ingress?.body ?? ''} styling={{ fontSize: 'large' }} />
      </Container>
      <Container>
        <Link fontSize={'medium'} to={links.payrollHelp}>
          {t('manualReporting.start.helpPageLink')}
        </Link>
      </Container>
    </IntroText>
  );
};

const StartPageWithoutEmployments = () => {
  return <IntroTextWithoutEmployments />;
};

const IntroTextWithoutEmployments = () => {
  const { t } = useTypedTranslation();
  const isAnyReportProcessing = useAppSelector(state => state.payroll.isAnyReportProcessing);
  const faqsNoReports = useAppSelector(state => state.content.content.faqListPayrollsNoReports);
  const ingressNoReports = useAppSelector(state => state.content.content.ingressPayrollsNoReports);
  const isDesktop = useMediaMin(theme.breakpoint.lg);

  return (
    <>
      <IntroText>
        <Container>
          <ParsedHtmlContent htmlContent={ingressNoReports?.body ?? ''} styling={{ fontSize: 'large' }} />
        </Container>

        <Container marginBottom={40}>
          <Link fontSize="medium" to={links.payrollHelp}>
            {t('manualReporting.start.helpPageLink')}
          </Link>
        </Container>
        {isAnyReportProcessing && <ReportProcessingFeedback />}
      </IntroText>

      {!!faqsNoReports.length && (
        <FAQ testId="payrolls-faq" layout={isDesktop ? 'drawer' : 'modal'} questionAnswers={faqsNoReports} />
      )}
    </>
  );
};
